import {
  pgTable, pgEnum, uuid, text, integer, bigint, numeric, boolean, timestamp, unique, jsonb,
} from "drizzle-orm/pg-core";
import type { AnyPgColumn } from "drizzle-orm/pg-core";
import type { ExtractedReceipt } from "@briolin/shared";

export const legalType = pgEnum("legal_type", ["физлицо", "ИП", "юрлицо"]);
export const priceTypeKind = pgEnum("price_type_kind", ["wholesale", "retail", "individual"]);

export const organizations = pgTable("organizations", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(),
  inn: text("inn"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const warehouses = pgTable("warehouses", {
  id: uuid("id").primaryKey().defaultRandom(),
  organizationId: uuid("organization_id").references(() => organizations.id),
  name: text("name").notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  legacy1cKey: text("legacy_1c_key"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const priceTypes = pgTable("price_types", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(),
  kind: priceTypeKind("kind").notNull(),
  legacy1cKey: text("legacy_1c_key"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const counterparties = pgTable("counterparties", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(),
  legalType: legalType("legal_type"),
  isClient: boolean("is_client").default(false).notNull(),
  isSupplier: boolean("is_supplier").default(false).notNull(),
  isReferralPartner: boolean("is_referral_partner").default(false).notNull(),
  segment: text("segment"),
  priceTypeId: uuid("price_type_id").references(() => priceTypes.id),
  discountPercent: numeric("discount_percent", { precision: 5, scale: 2 }).default("0").notNull(),
  inn: text("inn"),
  phone: text("phone"),
  email: text("email"),
  contactPerson: text("contact_person"),
  deliveryAddress: text("delivery_address"),
  legacy1cKey: text("legacy_1c_key"),
  referredByCounterpartyId: uuid("referred_by_counterparty_id")
    .references((): AnyPgColumn => counterparties.id),
  commissionRateOverride: numeric("commission_rate_override", { precision: 5, scale: 4 }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export const productCategories = pgTable("product_categories", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(),
  parentId: uuid("parent_id"),
  legacy1cKey: text("legacy_1c_key"),
});

export const products = pgTable("products", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(),
  sku: text("sku"),
  categoryId: uuid("category_id").references(() => productCategories.id),
  unit: text("unit"),
  isActive: boolean("is_active").default(true).notNull(),
  legacy1cKey: text("legacy_1c_key"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const productPrices = pgTable("product_prices", {
  id: uuid("id").primaryKey().defaultRandom(),
  productId: uuid("product_id").references(() => products.id).notNull(),
  priceTypeId: uuid("price_type_id").references(() => priceTypes.id).notNull(),
  priceKopecks: bigint("price_kopecks", { mode: "number" }).notNull(),
}, (t) => ({ uniqProductType: unique().on(t.productId, t.priceTypeId) }));

export const cashAccounts = pgTable("cash_accounts", {
  id: uuid("id").primaryKey().defaultRandom(),
  organizationId: uuid("organization_id").references(() => organizations.id),
  name: text("name").notNull(),
  kind: text("kind").notNull(), // 'касса' | 'банк'
  legacy1cKey: text("legacy_1c_key"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const cashflowCategories = pgTable("cashflow_categories", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(),
});

export const docType = pgEnum("doc_type", [
  "sales_order", "shipment", "client_payment", "supplier_receipt",
  "supplier_payment", "transfer", "inventory", "commission_accrual",
  "barber_payout", "other_cash", "opening_balance",
]);
export const stockMovementKind = pgEnum("stock_movement_kind", ["physical", "reserve"]);
export const orderStatus = pgEnum("order_status", ["open", "partially_shipped", "closed", "cancelled"]);

export const salesOrders = pgTable("sales_orders", {
  id: uuid("id").primaryKey().defaultRandom(),
  counterpartyId: uuid("counterparty_id").references(() => counterparties.id).notNull(),
  organizationId: uuid("organization_id").references(() => organizations.id),
  status: orderStatus("status").default("open").notNull(),
  number: text("number"),
  date: timestamp("date", { withTimezone: true }).defaultNow().notNull(),
  comment: text("comment"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const salesOrderLines = pgTable("sales_order_lines", {
  id: uuid("id").primaryKey().defaultRandom(),
  orderId: uuid("order_id").references(() => salesOrders.id).notNull(),
  productId: uuid("product_id").references(() => products.id).notNull(),
  warehouseId: uuid("warehouse_id").references(() => warehouses.id).notNull(),
  qty: integer("qty").notNull(),
  priceKopecks: bigint("price_kopecks", { mode: "number" }).notNull(),
  isPriceOverridden: boolean("is_price_overridden").default(false).notNull(),
});

export const shipments = pgTable("shipments", {
  id: uuid("id").primaryKey().defaultRandom(),
  orderId: uuid("order_id").references(() => salesOrders.id),
  counterpartyId: uuid("counterparty_id").references(() => counterparties.id).notNull(),
  organizationId: uuid("organization_id").references(() => organizations.id),
  number: text("number"),
  date: timestamp("date", { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const shipmentLines = pgTable("shipment_lines", {
  id: uuid("id").primaryKey().defaultRandom(),
  shipmentId: uuid("shipment_id").references(() => shipments.id).notNull(),
  productId: uuid("product_id").references(() => products.id).notNull(),
  warehouseId: uuid("warehouse_id").references(() => warehouses.id).notNull(),
  qty: integer("qty").notNull(),
  priceKopecks: bigint("price_kopecks", { mode: "number" }).notNull(),
});

export const clientPayments = pgTable("client_payments", {
  id: uuid("id").primaryKey().defaultRandom(),
  counterpartyId: uuid("counterparty_id").references(() => counterparties.id).notNull(),
  cashAccountId: uuid("cash_account_id").references(() => cashAccounts.id).notNull(),
  organizationId: uuid("organization_id").references(() => organizations.id),
  amountKopecks: bigint("amount_kopecks", { mode: "number" }).notNull(),
  cashflowCategoryId: uuid("cashflow_category_id").references(() => cashflowCategories.id),
  number: text("number"),
  date: timestamp("date", { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const stockMovements = pgTable("stock_movements", {
  id: uuid("id").primaryKey().defaultRandom(),
  productId: uuid("product_id").references(() => products.id).notNull(),
  warehouseId: uuid("warehouse_id").references(() => warehouses.id).notNull(),
  qtyDelta: integer("qty_delta").notNull(),
  kind: stockMovementKind("kind").notNull(),
  docType: docType("doc_type").notNull(),
  docId: uuid("doc_id").notNull(),
  date: timestamp("date", { withTimezone: true }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const settlementMovements = pgTable("settlement_movements", {
  id: uuid("id").primaryKey().defaultRandom(),
  counterpartyId: uuid("counterparty_id").references(() => counterparties.id).notNull(),
  amountDeltaKopecks: bigint("amount_delta_kopecks", { mode: "number" }).notNull(),
  docType: docType("doc_type").notNull(),
  docId: uuid("doc_id").notNull(),
  date: timestamp("date", { withTimezone: true }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const cashMovements = pgTable("cash_movements", {
  id: uuid("id").primaryKey().defaultRandom(),
  cashAccountId: uuid("cash_account_id").references(() => cashAccounts.id).notNull(),
  amountDeltaKopecks: bigint("amount_delta_kopecks", { mode: "number" }).notNull(),
  cashflowCategoryId: uuid("cashflow_category_id").references(() => cashflowCategories.id),
  docType: docType("doc_type").notNull(),
  docId: uuid("doc_id").notNull(),
  date: timestamp("date", { withTimezone: true }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

// --- backoffice (stage/backoffice) additions ---
export const auditAction = pgEnum("audit_action", ["post", "unpost"]);

export const postingAuditLog = pgTable("posting_audit_log", {
  id: uuid("id").primaryKey().defaultRandom(),
  actorId: uuid("actor_id").notNull(),
  docType: docType("doc_type").notNull(),
  docId: uuid("doc_id").notNull(),
  action: auditAction("action").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const inventorySessionStatus = pgEnum("inventory_session_status", ["counting", "posted"]);

export const inventorySessions = pgTable("inventory_sessions", {
  id: uuid("id").primaryKey().defaultRandom(),
  warehouseId: uuid("warehouse_id").references(() => warehouses.id).notNull(),
  status: inventorySessionStatus("status").default("counting").notNull(),
  date: timestamp("date", { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const inventorySessionLines = pgTable("inventory_session_lines", {
  id: uuid("id").primaryKey().defaultRandom(),
  sessionId: uuid("session_id").references(() => inventorySessions.id).notNull(),
  productId: uuid("product_id").references(() => products.id).notNull(),
  countedQty: integer("counted_qty").notNull(),
}, (t) => ({ uniqSessionProduct: unique().on(t.sessionId, t.productId) }));

export const supplierReceipts = pgTable("supplier_receipts", {
  id: uuid("id").primaryKey().defaultRandom(),
  supplierId: uuid("supplier_id").references(() => counterparties.id).notNull(),
  organizationId: uuid("organization_id").references(() => organizations.id),
  number: text("number"),
  date: timestamp("date", { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const supplierReceiptLines = pgTable("supplier_receipt_lines", {
  id: uuid("id").primaryKey().defaultRandom(),
  receiptId: uuid("receipt_id").references(() => supplierReceipts.id).notNull(),
  productId: uuid("product_id").references(() => products.id).notNull(),
  warehouseId: uuid("warehouse_id").references(() => warehouses.id).notNull(),
  qty: integer("qty").notNull(),
  costKopecks: bigint("cost_kopecks", { mode: "number" }).notNull(),
});

export const supplierPayments = pgTable("supplier_payments", {
  id: uuid("id").primaryKey().defaultRandom(),
  supplierId: uuid("supplier_id").references(() => counterparties.id).notNull(),
  cashAccountId: uuid("cash_account_id").references(() => cashAccounts.id).notNull(),
  amountKopecks: bigint("amount_kopecks", { mode: "number" }).notNull(),
  cashflowCategoryId: uuid("cashflow_category_id").references(() => cashflowCategories.id),
  number: text("number"),
  date: timestamp("date", { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const otherCashDocuments = pgTable("other_cash_documents", {
  id: uuid("id").primaryKey().defaultRandom(),
  cashAccountId: uuid("cash_account_id").references(() => cashAccounts.id).notNull(),
  amountKopecks: bigint("amount_kopecks", { mode: "number" }).notNull(), // + приход, − расход
  cashflowCategoryId: uuid("cashflow_category_id").references(() => cashflowCategories.id),
  description: text("description"),
  date: timestamp("date", { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const withdrawalStatus = pgEnum("withdrawal_status", ["pending", "approved", "rejected", "paid"]);

// --- storefront (stage/storefront) additions ---
export const authLinks = pgTable("auth_links", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id").notNull().unique(),
  counterpartyId: uuid("counterparty_id").references(() => counterparties.id).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const carts = pgTable("carts", {
  id: uuid("id").primaryKey().defaultRandom(),
  counterpartyId: uuid("counterparty_id").references(() => counterparties.id).notNull().unique(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export const cartItems = pgTable("cart_items", {
  id: uuid("id").primaryKey().defaultRandom(),
  cartId: uuid("cart_id").references(() => carts.id).notNull(),
  productId: uuid("product_id").references(() => products.id).notNull(),
  qty: integer("qty").notNull(),
}, (t) => ({ uniqCartProduct: unique().on(t.cartId, t.productId) }));

export const collectionStatus = pgEnum("collection_status", [
  "draft", "sent", "viewed", "ordered", "expired",
]);

export const collections = pgTable("collections", {
  id: uuid("id").primaryKey().defaultRandom(),
  barberCounterpartyId: uuid("barber_counterparty_id").references(() => counterparties.id).notNull(),
  refCode: text("ref_code").notNull().unique(),
  title: text("title").notNull(),
  status: collectionStatus("status").default("draft").notNull(),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const collectionItems = pgTable("collection_items", {
  id: uuid("id").primaryKey().defaultRandom(),
  collectionId: uuid("collection_id").references(() => collections.id).notNull(),
  productId: uuid("product_id").references(() => products.id).notNull(),
  qty: integer("qty").default(1).notNull(),
}, (t) => ({ uniqCollectionProduct: unique().on(t.collectionId, t.productId) }));

export const referralSettings = pgTable("referral_settings", {
  id: uuid("id").primaryKey().defaultRandom(),
  commissionRate: numeric("commission_rate", { precision: 5, scale: 4 }).default("0.2000").notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export const commissionAccruals = pgTable("commission_accruals", {
  id: uuid("id").primaryKey().defaultRandom(),
  barberCounterpartyId: uuid("barber_counterparty_id").references(() => counterparties.id).notNull(),
  sourceOrderId: uuid("source_order_id").references(() => salesOrders.id).notNull(),
  rate: numeric("rate", { precision: 5, scale: 4 }).notNull(),
  amountKopecks: bigint("amount_kopecks", { mode: "number" }).notNull(),
  date: timestamp("date", { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

// Unified withdrawal-request table (2026-07-02): merges the P1 "wallet" and
// "barber-payout" duplicates (withdrawal_requests / barber_withdrawal_requests)
// into the richer field union — cashAccountId/decidedBy/decidedAt from the
// approve/pay flow, barberCounterpartyId naming to match the rest of the schema
// (commissionAccruals, collections, wallet.ts) rather than the barber-payout-only "barberId".
export const withdrawalRequests = pgTable("withdrawal_requests", {
  id: uuid("id").primaryKey().defaultRandom(),
  barberCounterpartyId: uuid("barber_counterparty_id").references(() => counterparties.id).notNull(),
  amountKopecks: bigint("amount_kopecks", { mode: "number" }).notNull(),
  status: withdrawalStatus("status").default("pending").notNull(),
  cashAccountId: uuid("cash_account_id").references(() => cashAccounts.id),
  decidedBy: uuid("decided_by"),
  decidedAt: timestamp("decided_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const chatSenderRole = pgEnum("chat_sender_role", ["customer", "staff"]);

export const chatThreads = pgTable("chat_threads", {
  id: uuid("id").primaryKey().defaultRandom(),
  counterpartyId: uuid("counterparty_id").references(() => counterparties.id).notNull().unique(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const chatMessages = pgTable("chat_messages", {
  id: uuid("id").primaryKey().defaultRandom(),
  threadId: uuid("thread_id").references(() => chatThreads.id).notNull(),
  senderRole: chatSenderRole("sender_role").notNull(),
  body: text("body").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const pushSubscriptions = pgTable("push_subscriptions", {
  id: uuid("id").primaryKey().defaultRandom(),
  counterpartyId: uuid("counterparty_id").references(() => counterparties.id).notNull(),
  endpoint: text("endpoint").notNull().unique(),
  p256dh: text("p256dh").notNull(),
  authKey: text("auth_key").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const sbpPaymentStatus = pgEnum("sbp_payment_status", ["pending", "paid", "failed"]);

export const sbpPayments = pgTable("sbp_payments", {
  id: uuid("id").primaryKey().defaultRandom(),
  orderId: uuid("order_id").references(() => salesOrders.id).notNull().unique(),
  amountKopecks: bigint("amount_kopecks", { mode: "number" }).notNull(),
  providerPaymentId: text("provider_payment_id"),
  qrPayload: text("qr_payload"),
  status: sbpPaymentStatus("status").default("pending").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// --- analytics (stage/analytics) additions ---
export const salesFacts = pgTable("sales_facts", {
  id: uuid("id").primaryKey().defaultRandom(),
  saleDate: timestamp("sale_date", { withTimezone: true }).notNull(),
  counterpartyId: uuid("counterparty_id").references(() => counterparties.id).notNull(),
  productId: uuid("product_id").references(() => products.id).notNull(),
  warehouseId: uuid("warehouse_id").references(() => warehouses.id),
  qty: integer("qty").notNull(),
  amountKopecks: bigint("amount_kopecks", { mode: "number" }).notNull(),
  costKopecks: bigint("cost_kopecks", { mode: "number" }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export const savedQueries = pgTable("saved_queries", {
  id: uuid("id").primaryKey().defaultRandom(),
  title: text("title").notNull(),
  questionText: text("question_text").notNull(),
  sqlText: text("sql_text").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

// --- PDF-OCR receipts (plan 2026-07-01, Task 1) ---
// supplier_receipts/supplier_receipt_lines already exist above (backoffice stage) — reused as-is,
// field names supplierId/costKopecks kept (not counterpartyId/priceKopecks as originally drafted
// in the OCR plan) to avoid a second definition of the same document.
export const receiptImportStatus = pgEnum("receipt_import_status", [
  "draft", "extracted", "confirmed", "failed",
]);

export const receiptImports = pgTable("receipt_imports", {
  id: uuid("id").primaryKey().defaultRandom(),
  storagePath: text("storage_path").notNull(),
  status: receiptImportStatus("status").default("draft").notNull(),
  extractedJson: jsonb("extracted_json").$type<ExtractedReceipt>(),
  supplierName: text("supplier_name"),
  receiptId: uuid("receipt_id").references(() => supplierReceipts.id),
  error: text("error"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export const productMatchAliases = pgTable("product_match_aliases", {
  id: uuid("id").primaryKey().defaultRandom(),
  counterpartyId: uuid("counterparty_id").references(() => counterparties.id).notNull(),
  supplierProductName: text("supplier_product_name").notNull(),
  productId: uuid("product_id").references(() => products.id).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({ uniqSupplierName: unique().on(t.counterpartyId, t.supplierProductName) }));
